import { useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { useSearchProviders } from '../hooks'
import { IconSearch, IconX } from '@/app/components/icons'

interface SearchBarProps {
  readonly onSearch: (query: string, provider: string | undefined, resourceType: string | undefined) => void
}

const RESOURCE_TYPES = ['checkpoint', 'lora', 'vae', 'controlnet', 'upscaler', 'embedding'] as const

const TYPE_COLORS: Readonly<Record<string, string>> = {
  checkpoint: '#8b5cf6',
  lora: '#3b82f6',
  vae: '#10b981',
  controlnet: '#f59e0b',
  upscaler: '#ec4899',
  embedding: '#6366f1',
}

export function SearchBar({ onSearch }: SearchBarProps) {
  const { t } = useTranslation()
  const [inputValue, setInputValue] = useState('')
  const [debouncedQuery, setDebouncedQuery] = useState('')
  const [selectedProvider, setSelectedProvider] = useState<string | undefined>(undefined)
  const [selectedType, setSelectedType] = useState<string | undefined>(undefined)

  const providersQuery = useSearchProviders()
  const providers = providersQuery.data ?? []

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedQuery(inputValue)
    }, 500)
    return () => clearTimeout(timer)
  }, [inputValue])

  useEffect(() => {
    onSearch(debouncedQuery, selectedProvider, selectedType)
  }, [debouncedQuery, selectedProvider, selectedType, onSearch])

  return (
    <div className="flex flex-col gap-3">
      {/* Search input */}
      <div className="flex items-center gap-2 rounded-xl border border-tg-hint/20 bg-tg-secondary px-3 py-2.5 shadow-sm transition-all duration-150 focus-within:border-[var(--tg-theme-button-color)] focus-within:shadow-md">
        <IconSearch className="h-4 w-4 shrink-0 text-tg-hint" />
        <input
          type="text"
          value={inputValue}
          onChange={(e) => setInputValue(e.target.value)}
          placeholder={t('search.placeholder')}
          className="flex-1 bg-transparent text-sm text-tg-text outline-none placeholder:text-tg-hint"
        />
        {inputValue.length > 0 && (
          <button
            type="button"
            onClick={() => setInputValue('')}
            className="rounded-full p-0.5 text-tg-hint transition-colors hover:text-tg-text active:opacity-70"
          >
            <IconX className="h-3.5 w-3.5" />
          </button>
        )}
      </div>

      {/* Provider filter */}
      {providers.length > 0 && (
        <div className="flex gap-2 overflow-x-auto pb-1">
          <button
            type="button"
            onClick={() => setSelectedProvider(undefined)}
            className="shrink-0 rounded-full px-3 py-1.5 text-xs font-medium transition-all duration-150 active:scale-[0.96]"
            style={{
              backgroundColor: selectedProvider === undefined
                ? 'var(--tg-theme-button-color)'
                : 'color-mix(in srgb, var(--tg-theme-button-color) 8%, var(--tg-theme-secondary-bg-color))',
              color: selectedProvider === undefined
                ? 'var(--tg-theme-button-text-color)'
                : 'var(--tg-theme-text-color)',
              border: selectedProvider === undefined
                ? '1px solid var(--tg-theme-button-color)'
                : '1px solid transparent',
            }}
          >
            {t('search.allProviders')}
          </button>
          {providers.map((provider) => (
            <button
              key={provider.name}
              type="button"
              onClick={() => setSelectedProvider(provider.name)}
              className="shrink-0 rounded-full px-3 py-1.5 text-xs font-medium transition-all duration-150 active:scale-[0.96]"
              style={{
                backgroundColor: selectedProvider === provider.name
                  ? 'var(--tg-theme-button-color)'
                  : 'color-mix(in srgb, var(--tg-theme-button-color) 8%, var(--tg-theme-secondary-bg-color))',
                color: selectedProvider === provider.name
                  ? 'var(--tg-theme-button-text-color)'
                  : 'var(--tg-theme-text-color)',
                opacity: provider.is_configured ? 1 : 0.5,
                border: selectedProvider === provider.name
                  ? '1px solid var(--tg-theme-button-color)'
                  : '1px solid transparent',
              }}
            >
              {provider.name}
            </button>
          ))}
        </div>
      )}

      {/* Resource type filter pills */}
      <div className="flex gap-2 overflow-x-auto pb-1">
        <button
          type="button"
          onClick={() => setSelectedType(undefined)}
          className="shrink-0 rounded-full px-3 py-1.5 text-xs font-medium transition-all duration-150 active:scale-[0.96]"
          style={{
            backgroundColor: selectedType === undefined
              ? 'var(--tg-theme-button-color)'
              : 'color-mix(in srgb, var(--tg-theme-button-color) 8%, var(--tg-theme-secondary-bg-color))',
            color: selectedType === undefined
              ? 'var(--tg-theme-button-text-color)'
              : 'var(--tg-theme-text-color)',
            border: selectedType === undefined
              ? '1px solid var(--tg-theme-button-color)'
              : '1px solid transparent',
          }}
        >
          {t('search.allTypes')}
        </button>
        {RESOURCE_TYPES.map((type) => {
          const color = TYPE_COLORS[type] ?? '#6b7280'
          const isActive = selectedType === type
          return (
            <button
              key={type}
              type="button"
              onClick={() => setSelectedType(type)}
              className="shrink-0 rounded-full px-3 py-1.5 text-xs font-medium transition-all duration-150 active:scale-[0.96]"
              style={{
                backgroundColor: isActive ? `color-mix(in srgb, ${color} 20%, var(--tg-theme-secondary-bg-color))` : 'color-mix(in srgb, var(--tg-theme-button-color) 8%, var(--tg-theme-secondary-bg-color))',
                color: isActive ? color : 'var(--tg-theme-text-color)',
                border: isActive ? `1px solid ${color}` : '1px solid transparent',
              }}
            >
              {t(`resources.filter.${type}`)}
            </button>
          )
        })}
      </div>
    </div>
  )
}
